import { usePrimaryColor } from '../../branding/usePrimaryColor';

interface CancellationRateProps {
  cancelledCount: number;
  totalCount: number;
  rate: number | null;
  previousRate: number | null;
  loading?: boolean;
  error?: boolean;
  forbidden?: boolean;
  periodLabel?: string;
  onRetry?: () => void;
}

export function CancellationRate({
  cancelledCount,
  totalCount,
  rate,
  previousRate,
  loading = false,
  error = false,
  forbidden = false,
  periodLabel,
  onRetry,
}: CancellationRateProps) {
  const primaryColor = usePrimaryColor();

  // Forbidden state
  if (forbidden) {
    return (
      <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
        <div className="px-4 py-3 hair-b">
          <h3 className="text-[14px] font-semibold">Cancellation rate</h3>
        </div>
        <div className="px-4 pt-4 pb-3 flex items-center justify-center min-h-[120px]">
          <p className="text-ink-4 text-[13px]">You don't have access to this.</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
        <div className="px-4 py-3 hair-b">
          <h3 className="text-[14px] font-semibold">Cancellation rate</h3>
        </div>
        <div className="px-4 pt-4 pb-3">
          <p className="text-[13px] text-ink-4 mb-3">Couldn't load cancellation data.</p>
          {onRetry && (
            <button
              onClick={onRetry}
              className="text-[11.5px] font-medium text-tide hover:text-tide/80"
            >
              Retry
            </button>
          )}
        </div>
      </div>
    );
  }

  // Loading skeleton
  if (loading && totalCount === 0) {
    return (
      <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
        <div className="px-4 py-3 hair-b">
          <div className="h-4 w-32 bg-canvas animate-pulse rounded" />
        </div>
        <div className="px-4 pt-4 pb-3 space-y-3">
          <div className="h-8 w-20 bg-canvas animate-pulse rounded" />
          <div className="h-3 w-28 bg-canvas animate-pulse rounded" />
          <div className="h-2 w-full bg-canvas animate-pulse rounded mt-3" />
        </div>
      </div>
    );
  }

  // Empty state: no bookings in the period
  if (totalCount === 0) {
    return (
      <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
        <div className="px-4 py-3 hair-b">
          <div className="flex items-center gap-2">
            <h3 className="text-[14px] font-semibold">Cancellation rate</h3>
            {periodLabel && (
              <span className="text-[11.5px] text-ink-4">&middot; {periodLabel.toLowerCase()}</span>
            )}
          </div>
        </div>
        <div className="px-4 pt-4 pb-3 flex flex-col items-center justify-center min-h-[120px] gap-2">
          <p className="text-[11.5px] text-ink-4">
            No bookings in this period to measure.
          </p>
        </div>
      </div>
    );
  }

  const ratePct = rate !== null ? (rate * 100) : 0;
  const prevPct = previousRate !== null ? (previousRate * 100) : null;
  const delta = prevPct !== null ? ratePct - prevPct : null;

  // Color: low cancellation = good (sage), high = concerning (rust)
  const rateColor = ratePct <= 5 ? 'text-sage' : ratePct <= 15 ? 'text-ink-2' : 'text-rust';
  // Bar fill: use primary color at varying opacity based on rate
  const barPct = Math.min(ratePct, 100);

  return (
    <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 hair-b">
        <div className="flex items-center gap-2">
          <h3 className="text-[14px] font-semibold">Cancellation rate</h3>
          {periodLabel && (
            <span className="text-[11.5px] text-ink-4">&middot; {periodLabel.toLowerCase()}</span>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="px-4 pt-4 pb-3">
        {/* Main rate */}
        <div className="flex items-baseline gap-2 mb-1">
          <span className={`text-[28px] font-semibold leading-none num ${rateColor}`}>
            {ratePct.toFixed(1)}%
          </span>
          {delta !== null && (
            <span className={`text-[11.5px] font-medium ${delta > 0 ? 'text-rust' : delta < 0 ? 'text-sage' : 'text-ink-4'}`}>
              {delta > 0 ? '↑' : delta < 0 ? '↓' : '→'} {Math.abs(delta).toFixed(1)}pp
            </span>
          )}
        </div>

        {/* Counts */}
        <p className="text-[11.5px] text-ink-4 mb-4">
          {cancelledCount} cancelled of {totalCount} booking{totalCount !== 1 ? 's' : ''}
        </p>

        {/* Visual bar */}
        <div className="h-2 rounded-full bg-canvas overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-300"
            style={{
              width: `${Math.max(barPct, cancelledCount > 0 ? 2 : 0)}%`,
              backgroundColor: ratePct <= 5 ? 'var(--sage)' : ratePct <= 15 ? primaryColor : 'var(--rust)',
              opacity: 0.7,
            }}
          />
        </div>

        {/* Previous period context */}
        {prevPct !== null && (
          <p className="text-[10.5px] text-ink-4 mt-2">
            Previous period: {prevPct.toFixed(1)}%
          </p>
        )}
      </div>
    </div>
  );
}
